import { when } from 'mobx';
import type { Account, UserProfileForm } from '@linktivity/ars-neo';
import type { AppCapabilities } from '@link/share/stores';
import { isProfileComplete } from './formRules';
import { buildPathWithRedirect } from './redirect';
import { buildLocalizedPath } from './url';

/**
 * Build a localized profile-page URL that redirects back to
 * `localizedReturnPath` (already locale-prefixed) after save.
 */
export const buildProfilePathWithRedirect = (
  localizedReturnPath: string
): string => {
  return buildPathWithRedirect(
    buildLocalizedPath('profile'),
    localizedReturnPath
  );
};

// The API stores an unset birthday as 1980-01-01T01:01:01Z, so any birthday
// at minute 1 and second 1 (UTC) is treated as that placeholder.
export const isPlaceholderBirthday = (birthday?: Date | null): boolean =>
  !!birthday &&
  birthday.getUTCMinutes() === 1 &&
  birthday.getUTCSeconds() === 1;

// Run on every account read from the API so the form, the completeness gate
// and participant prefill all see the placeholder as empty.
export const clearPlaceholderBirthday = <
  TAccount extends Pick<Account, 'birthday'>
>(
  account: TAccount
): TAccount =>
  isPlaceholderBirthday(account.birthday)
    ? { ...account, birthday: undefined }
    : account;

// The postcode flag comes from AppCapabilities so the gate and the form share
// one required field.
export interface ProfileGuardSiteStore
  extends Pick<AppCapabilities, 'profilePostCodeEnabled'> {
  readonly siteSchema?: object;
  readonly userProfileForm?: UserProfileForm;
  readonly siteLoading?: boolean;
  readonly siteSettled?: boolean;
}

export const isProfileCompleteForSite = (
  account: Account,
  siteStore: ProfileGuardSiteStore
): boolean =>
  isProfileComplete(account, siteStore.userProfileForm, {
    requireJapanPostCode: siteStore.profilePostCodeEnabled
  });

export interface ProfileGuardOptions {
  accountStore: {
    readonly accountInfo?: Account;
    getAccountInfo: () => Promise<Account>;
  };
  siteStore: ProfileGuardSiteStore;
}

// The account stores initialize `accountInfo` to `{}` before the fetch, so a
// populated object is the only signal that the account has actually loaded.
export const hasLoadedAccount = <TAccount>(
  account?: TAccount
): account is TAccount =>
  !!account &&
  typeof account === 'object' &&
  Object.keys(account as object).length > 0;

// `siteLoading` starts out false before the layout effect kicks off the
// fetch, so "not loading" alone can be observed before the schema exists;
// require a populated schema as well.
const hasLoadedSite = (site: ProfileGuardSiteStore): boolean =>
  !site.siteLoading &&
  !!site.siteSchema &&
  Object.keys(site.siteSchema).length > 0;

// The schema fetch has settled at least once (success or failure) — the
// signal that distinguishes "failed, schema left empty" from "not fetched
// yet". Once settled, an empty schema is still evaluable: the absent field
// modes default to required, so profile completeness remains fail-closed.
const hasSettledSite = (site: ProfileGuardSiteStore): boolean =>
  !site.siteLoading && !!site.siteSettled;

// The profile rules are final: the schema landed, or its fetch settled without
// one (then every field counts as required).
export const isProfileGateEvaluable = (site: ProfileGuardSiteStore): boolean =>
  hasLoadedSite(site) || hasSettledSite(site);

// The pre-order gate's verdict: the rules are final and the account meets
// them. No account never passes.
export const isProfileGatePassed = (
  account: Account | null | undefined,
  site: ProfileGuardSiteStore
): boolean =>
  !!account &&
  isProfileGateEvaluable(site) &&
  isProfileCompleteForSite(account, site);

// Upper bound on waiting for the site schema so a fetch that never settles
// cannot strand the user on the login page. On timeout this post-auth guard
// continues to the requested route; BookConfirm remains the fail-closed
// backstop and keeps payment disabled until its profile gate is evaluable.
const SITE_SCHEMA_WAIT_TIMEOUT_MS = 10_000;

/**
 * Post-auth profile-completeness gate. Returns a localized profile-page URL
 * (with `redirectTo` as the return target) when the logged-in account is
 * missing fields the site config marks as required, or `redirectTo` itself
 * when navigation can proceed directly. Account fetch failures never block
 * navigation; the pre-order gate in BookConfirm remains the backstop.
 */
export const resolveProfileGuardPath = async (
  guard: ProfileGuardOptions,
  redirectTo: string
): Promise<string> => {
  // Already heading to the profile page: gating would only produce a
  // profile -> profile redirect, so skip the account lookup entirely.
  const profilePath = buildLocalizedPath('profile');
  if (redirectTo === profilePath || redirectTo.startsWith(`${profilePath}?`)) {
    return redirectTo;
  }
  try {
    // Reuse the account already in the store when present: logout resets
    // accountInfo to {} in every app layout, so a populated value always
    // belongs to the current session's user.
    const cached = guard.accountStore.accountInfo;
    const [account] = await Promise.all([
      hasLoadedAccount(cached) ? cached : guard.accountStore.getAccountInfo(),
      // Wait until the site schema fetch settles. Evaluating while it is still
      // in flight would treat every absent mode as required and could redirect
      // a profile that the eventual schema marks optional or hidden. A
      // settled-but-empty schema intentionally uses the all-required fallback.
      when(() => isProfileGateEvaluable(guard.siteStore), {
        timeout: SITE_SCHEMA_WAIT_TIMEOUT_MS
      })
    ]);
    if (!isProfileCompleteForSite(account, guard.siteStore)) {
      return buildProfilePathWithRedirect(redirectTo);
    }
  } catch {
    // Ignore and continue to the original target.
  }
  return redirectTo;
};
